import { Asset } from '@/api/generatedApi';
import { AssetNameWithExtension } from '@/components/AssetNameWithExtension';
import { AssetDetailsCell } from '@/components/ContentsTable/cellDefinitions';
import { Ellipsis } from '@/components/Ellipsis';
import { MatchAsset } from '@/components/Search/SearchTable';
import { Thumbnail } from '@/components/Thumbnail';
import { ThumbnailPreview } from '@/components/ThumbnailPreview';
import { PARAM_NAMES, useSearchFilters } from '@/hooks/useSearchFilters';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useTenants } from '@/services/tenants/useTenants';
import { MatchData } from '@/types/matchData';
import { getAssetName } from '@/utils/assets';
import { getAssetStateDisplayText } from '@/utils/assetStateDisplayText';
import { dateFormat, formatMatchPercentage } from '@/utils/format';
import { Box, LinearProgress, styled } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { getFileExtension } from '@physna/supported-files';
import { Link } from 'react-router-dom';
import { SearchType } from '../types';

const PercentagePillContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: theme.palette.divider,
  borderRadius: 12,
  padding: '2px 6px',
  minWidth: '75px',
  height: '16px',
  display: 'inline-block',
  overflow: 'hidden',
}));

const PercentagePillFill = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  background: theme.palette.secondary.main,
  borderRadius: 0,
  transition: 'width 0.3s ease',
}));

const PercentagePillText = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '6px',
  transform: 'translateY(-50%)',
  fontWeight: 600,
  fontSize: '0.5rem',
  lineHeight: 1,
  color: theme.palette.common.white,
  zIndex: 1,
  textShadow: `0 0 2px ${theme.palette.secondary.main}`,
}));

const handleLinkClick = (event: React.MouseEvent) => {
  event.stopPropagation();
};

export const RenderName = (parameters: GridRenderCellParams<MatchAsset>) => {
  const { getPath } = useTenants();
  const { searchTabs } = useSearchTabs();

  const assetTenant = searchTabs.find(tenant => tenant?.tenantId === parameters.row.tenantId);
  const query = assetTenant ? `?${PARAM_NAMES.TENANT}=${assetTenant?.tenantShortName}` : '';

  return (
    <Link
      to={getPath(`asset/${parameters.row.id}${query}`)}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={handleLinkClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
        <ThumbnailPreview primaryAsset={parameters.row} placement='right' message={getAssetName(parameters.row)}>
          <Box sx={{ cursor: 'pointer' }}>
            <Thumbnail size={42} asset={parameters.row} noLink={true} />
          </Box>
        </ThumbnailPreview>
        <AssetNameWithExtension
          name={getAssetName(parameters.row)}
          path={parameters.row.path}
          extension={getFileExtension(parameters.row.path)}
          showAssemblyPill={parameters.row.isAssembly}
          isRootAssembly={parameters.row.isRootAssembly}
          isMissingDependencies={parameters.row.isMissingDependencies}
        />
      </Box>
    </Link>
  );
};

export const RenderDetails = (
  parameters: GridRenderCellParams<MatchAsset>,
  canOnlySearch?: boolean,
  isCrossTenant?: boolean,
  searchType?: SearchType
) => {
  const { assetId: matchAssetId, minThreshold } = useSearchFilters();
  const showFindMatchesButton = !canOnlySearch && !isCrossTenant;

  const matchData: MatchData = {
    searchType,
    matchPercentage: parameters.row.matchPercentage,
    forwardMatchPercentage: parameters.row.forwardMatchPercentage,
    reverseMatchPercentage: parameters.row.reverseMatchPercentage,
  };

  return parameters.row.state === 'finished' ? (
    <AssetDetailsCell
      asset={parameters.row}
      quickCompareAssetId={matchAssetId}
      showFindMatchesButton={showFindMatchesButton}
      matchData={matchData}
      minThreshold={minThreshold}
    />
  ) : (
    <Box display='flex' flexDirection='row' alignItems='center' gap={'8px'}>
      {parameters.row.state === 'indexing' && <LinearProgress sx={{ width: '38px' }} />}
      <Box overflow='hidden'>
        <Ellipsis text={getAssetStateDisplayText(parameters.row.state)} />
      </Box>
    </Box>
  );
};

export const RenderCreatedAt = (parameters: GridRenderCellParams<MatchAsset>) => (
  <Ellipsis text={dateFormat(parameters.row.createdAt)} />
);

export const RenderPercentage = ({
  value,
  message,
  searchAsset,
  matchAsset,
}: {
  value?: number;
  message?: string;
  searchAsset?: Asset;
  matchAsset?: MatchAsset;
}) => {
  if (!value) return;

  const displayValue = formatMatchPercentage(value);
  const messageValue = formatMatchPercentage(value, 10);

  return (
    <ThumbnailPreview
      placement='bottom'
      message={message ?? `Asset geometries match ${messageValue}%`}
      primaryAsset={searchAsset}
      secondaryAsset={matchAsset}
    >
      <PercentagePillContainer>
        <PercentagePillFill style={{ width: `${Math.min(Number(displayValue), 100)}%` }} />
        <PercentagePillText>{displayValue}%</PercentagePillText>
      </PercentagePillContainer>
    </ThumbnailPreview>
  );
};

export const RenderMatchPercentage = (parameters: GridRenderCellParams<MatchAsset>, searchAsset?: Asset) => (
  <RenderPercentage value={parameters.row.matchPercentage} searchAsset={searchAsset} matchAsset={parameters.row} />
);

export const RenderForwardMatchPercentage = (parameters: GridRenderCellParams<MatchAsset>, searchAsset?: Asset) => (
  <RenderPercentage
    searchAsset={searchAsset}
    matchAsset={parameters.row}
    value={parameters.row.forwardMatchPercentage}
    message={`${parameters.row.forwardMatchPercentage}% of asset is in match`}
  />
);

export const RenderReverseMatchPercentage = (parameters: GridRenderCellParams<MatchAsset>, searchAsset?: Asset) => {
  const percentage = parameters.row.reverseMatchPercentage;
  const message =
    percentage && percentage > 80 ? `Asset contains ${percentage}% of match` : `Asset makes up ${percentage}% of match`;
  return (
    <RenderPercentage searchAsset={searchAsset} matchAsset={parameters.row} value={percentage} message={message} />
  );
};
